import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, toast } from '@utima/ui';
import { AlertTriangle } from 'lucide-react';
import { memo } from 'react';

import type { KBFolderDeleteTarget } from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';

export type DeleteFoldersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: KBFolderDeleteTarget[];
  mutations: ReturnType<typeof useKBMutations>;
  onDeleted?: () => void;
};

export const DeleteFoldersModal = memo(function DeleteFoldersModal({
  open,
  onOpenChange,
  folders,
  mutations,
  onDeleted,
}: DeleteFoldersModalProps) {
  const { t } = useLingui();
  const isBulk = folders.length > 1;
  const singleFolder = folders.length === 1 ? folders[0] : undefined;

  const handleDelete = async () => {
    try {
      for (const folder of folders) {
        await mutations.deleteFolder.mutateAsync({
          id: folder.id,
          strategy: 'cascade',
        });
      }

      onOpenChange(false);
      onDeleted?.();

      if (isBulk) {
        toast.success(t`Folders deleted`);
      } else {
        toast.success(t`Folder deleted`);
      }
    } catch {
      toast.error(t`Failed to delete folder`);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[500px]'>
        <Dialog.Header>
          <Dialog.Title className='flex items-center gap-2 text-danger'>
            <AlertTriangle className='size-5' />
            {isBulk ? (
              <Trans>Delete folders</Trans>
            ) : (
              <Trans>Delete Folder</Trans>
            )}
          </Dialog.Title>
          <Dialog.Description>
            {isBulk ? (
              <Trans>
                Are you sure you want to delete {folders.length} folders? All
                documents, subfolders, and their contents will be permanently
                deleted.
              </Trans>
            ) : (
              <Trans>
                Are you sure you want to delete &quot;{singleFolder?.name}
                &quot;? All documents, subfolders, and their contents will be
                permanently deleted.
              </Trans>
            )}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant='danger'
            onClick={handleDelete}
            loading={mutations.deleteFolder.isPending}
          >
            <Trans>Delete</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
